import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Row, Col, Card, Table, Form, Badge, Spinner, Alert, Button, Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import PageLayout from "../../../../layouts/PageLayout";
import { apiGet, apiPost, apiPut, errorText, rs, date, PAYOUT_STATUS_VARIANT } from "../../../Seller/sellerApi";

export default function SellerPayoutsPage() {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({});
    const [counts, setCounts] = useState({});
    const [labels, setLabels] = useState({});
    const [sellers, setSellers] = useState([]);
    const [status, setStatus] = useState("eligible");
    const [sellerId, setSellerId] = useState("");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [checked, setChecked] = useState([]);
    const [transfer, setTransfer] = useState(null); // { methods, banks, form }
    const [deduction, setDeduction] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await apiGet("/admin/seller-payouts", {
                status: status || undefined,
                store_seller_id: sellerId || undefined,
                search: search || undefined,
            });
            setRows(res.data?.data || []);
            setSummary(res.data?.summary || {});
            setCounts(res.data?.status_counts || {});
            setLabels(res.data?.status_labels || {});
            setSellers(res.data?.sellers || []);
            setChecked([]);
        } catch (e) {
            setError(errorText(e, "Could not load payouts."));
        } finally {
            setLoading(false);
        }
    }, [status, sellerId, search]);

    useEffect(() => {
        const t = setTimeout(load, 300);
        return () => clearTimeout(t);
    }, [load]);

    const selectedRows = useMemo(() => rows.filter((r) => checked.includes(r.id)), [rows, checked]);
    const selectedTotal = selectedRows.reduce((s, r) => s + Number(r.net_pkr || 0), 0);
    const toggle = (id) => setChecked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    const selectable = (r) => r.status === "eligible" || r.status === "approved";

    const approve = async () => {
        setBusy(true);
        try {
            const res = await apiPost("/admin/seller-payouts/approve", { ids: checked });
            Swal.fire({ icon: "success", text: res.data?.message, timer: 2000, showConfirmButton: false });
            load();
        } catch (e) {
            Swal.fire({ icon: "error", text: errorText(e) });
        } finally {
            setBusy(false);
        }
    };

    const openTransfer = async () => {
        const sellerIds = [...new Set(selectedRows.map((r) => r.store_seller_id))];
        if (sellerIds.length !== 1) {
            Swal.fire({ icon: "info", text: "Select approved items of one seller to record a transfer." });
            return;
        }
        try {
            const res = await apiGet(`/admin/seller-payouts/seller/${sellerIds[0]}/methods`);
            const methods = res.data?.data || [];
            const def = methods.find((m) => m.is_default) || methods[0];
            setTransfer({
                seller: selectedRows[0].seller_name,
                methods,
                banks: res.data?.banks || [],
                form: { payment_method_id: def?.id || "", reference: "", payment_date: new Date().toISOString().slice(0, 10), bank_id: "", notes: "" },
            });
        } catch (e) {
            Swal.fire({ icon: "error", text: errorText(e) });
        }
    };

    const saveTransfer = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const f = transfer.form;
            const res = await apiPost("/admin/seller-payouts/transfer", {
                ids: checked,
                payment_method_id: f.payment_method_id || null,
                reference: f.reference,
                payment_date: f.payment_date || null,
                bank_id: f.bank_id || null,
                notes: f.notes || null,
            });
            setTransfer(null);
            Swal.fire({ icon: "success", text: res.data?.message });
            load();
        } catch (err) {
            Swal.fire({ icon: "error", text: errorText(err) });
        } finally {
            setBusy(false);
        }
    };

    const saveDeduction = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await apiPut(`/admin/seller-payouts/${deduction.id}/deductions`, {
                other_deductions_pkr: deduction.amount || 0,
                deductions_note: deduction.note || null,
            });
            setDeduction(null);
            load();
        } catch (err) {
            Swal.fire({ icon: "error", text: errorText(err) });
        } finally {
            setBusy(false);
        }
    };

    const sync = async () => {
        setBusy(true);
        try {
            const res = await apiPost("/admin/seller-payouts/sync");
            Swal.fire({ icon: "success", text: res.data?.message, timer: 2000, showConfirmButton: false });
            load();
        } catch (e) {
            Swal.fire({ icon: "error", text: errorText(e) });
        } finally {
            setBusy(false);
        }
    };

    const setTransferField = (k, v) => setTransfer((t) => ({ ...t, form: { ...t.form, [k]: v } }));

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">Seller Payouts</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to="/dashboard" className="mc-breadcrumb-link">Home</Link></li>
                                <li className="mc-breadcrumb-item">Seller Payouts</li>
                            </ul>
                        </div>
                        <Button variant="secondary" size="sm" onClick={sync} disabled={busy}>Rebuild from orders</Button>
                    </div>
                </Col>

                <Col xl={12}>
                    <Row className="g-3 mb-3">
                        {[
                            ["On hold", Number(summary.on_hold_pkr || 0)],
                            ["Complaint hold", summary.complaint_hold_pkr],
                            ["Ready for payout", summary.eligible_pkr],
                            ["Approved, not paid", summary.approved_pkr],
                            ["Paid", summary.paid_pkr],
                        ].map(([label, v]) => (
                            <Col key={label} xs={6} lg>
                                <div className="mc-card p-3 bg-white rounded-3 shadow-sm border h-100">
                                    <div className="text-muted small">{label}</div>
                                    <div className="fw-bold fs-5">{rs(v)}</div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Col>

                <Col xl={12}>
                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Body className="p-4">
                            <p className="text-muted small">
                                Payments are held 60 days from the sale (the first 14 days are the complaint window). An open complaint keeps the
                                hold. After that an item is ready for payout: approve it, pay the seller, then record the transfer.
                            </p>
                            <Row className="g-2 mb-3">
                                <Col md={3}>
                                    <Form.Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
                                        <option value="">All statuses</option>
                                        {Object.entries(labels).map(([k, l]) => (
                                            <option key={k} value={k}>{l}{counts[k] ? ` (${counts[k]})` : ""}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md={3}>
                                    <Form.Select value={sellerId} onChange={(e) => setSellerId(e.target.value)} aria-label="Seller">
                                        <option value="">All sellers</option>
                                        {sellers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </Form.Select>
                                </Col>
                                <Col md={3}>
                                    <Form.Control placeholder="Order or product" value={search} onChange={(e) => setSearch(e.target.value)} />
                                </Col>
                                <Col md={3} className="d-flex gap-2 justify-content-md-end">
                                    <Button disabled={busy || !selectedRows.some((r) => r.status === "eligible")} onClick={approve}>Approve</Button>
                                    <Button variant="success" disabled={busy || !selectedRows.length || selectedRows.some((r) => r.status !== "approved")} onClick={openTransfer}>
                                        Record transfer
                                    </Button>
                                </Col>
                            </Row>
                            {checked.length > 0 && <p className="small mb-2">{checked.length} selected · {rs(selectedTotal)}</p>}
                            {error && <Alert variant="danger">{error}</Alert>}
                            {loading ? (
                                <div className="text-center py-5"><Spinner animation="border" /></div>
                            ) : rows.length === 0 ? (
                                <p className="text-muted text-center py-4 mb-0">Nothing here.</p>
                            ) : (
                                <Table hover responsive className="align-middle mb-0 small">
                                    <thead>
                                        <tr>
                                            <th></th><th>Seller</th><th>Order / product</th><th>Sold</th><th>Gross</th><th>Fee</th><th>Deductions</th><th>Net</th><th>Hold until</th><th>Status</th><th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => (
                                            <tr key={r.id}>
                                                <td>
                                                    {selectable(r) && (
                                                        <Form.Check type="checkbox" checked={checked.includes(r.id)} onChange={() => toggle(r.id)} aria-label={`Select ${r.order_number}`} />
                                                    )}
                                                </td>
                                                <td>{r.seller_name}</td>
                                                <td><strong>{r.order_number}</strong><span className="d-block text-muted">{r.product_code || r.title}</span></td>
                                                <td>{date(r.sold_at)}<span className="d-block text-muted">{r.order_payment_status} · {r.delivery_status}</span></td>
                                                <td>{rs(r.gross_pkr)}</td>
                                                <td>{rs(r.fee_pkr)}<span className="d-block text-muted">{r.fee_percent}%</span></td>
                                                <td>{rs(Number(r.shipping_deduction_pkr || 0) + Number(r.other_deductions_pkr || 0))}{r.deductions_note && <span className="d-block text-muted">{r.deductions_note}</span>}</td>
                                                <td className="fw-semibold">{rs(r.net_pkr)}</td>
                                                <td>{r.hold_until}{r.days_left > 0 && <span className="d-block text-muted">{r.days_left} days left</span>}</td>
                                                <td>
                                                    <Badge bg={PAYOUT_STATUS_VARIANT[r.status] || "secondary"} title={r.hold_reason || ""}>{r.status_label}</Badge>
                                                    {r.transfer_reference && <span className="d-block text-muted">Ref {r.transfer_reference}</span>}
                                                </td>
                                                <td className="text-end">
                                                    {r.status !== "paid" && r.status !== "cancelled" && (
                                                        <Button size="sm" variant="link" onClick={() => setDeduction({ id: r.id, amount: r.other_deductions_pkr || "", note: r.deductions_note || "" })}>
                                                            Adjust
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={!!transfer} onHide={() => setTransfer(null)} centered>
                {transfer && (
                    <Form onSubmit={saveTransfer}>
                        <Modal.Header closeButton><Modal.Title>Record transfer · {transfer.seller}</Modal.Title></Modal.Header>
                        <Modal.Body>
                            <p>Amount: <strong>{rs(selectedTotal)}</strong> for {selectedRows.length} item(s).</p>
                            <Form.Group className="mb-2">
                                <Form.Label>Paid to</Form.Label>
                                <Form.Select value={transfer.form.payment_method_id} onChange={(e) => setTransferField("payment_method_id", e.target.value)}>
                                    <option value="">Not specified</option>
                                    {transfer.methods.map((m) => <option key={m.id} value={m.id}>{m.summary}{m.is_default ? " (default)" : ""}</option>)}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label>Transfer reference *</Form.Label>
                                <Form.Control required value={transfer.form.reference} onChange={(e) => setTransferField("reference", e.target.value)} />
                            </Form.Group>
                            <Row className="g-2">
                                <Col>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Date</Form.Label>
                                        <Form.Control type="date" value={transfer.form.payment_date} onChange={(e) => setTransferField("payment_date", e.target.value)} />
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-2">
                                        <Form.Label>Paid from (our bank)</Form.Label>
                                        <Form.Select value={transfer.form.bank_id} onChange={(e) => setTransferField("bank_id", e.target.value)}>
                                            <option value="">-</option>
                                            {transfer.banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Form.Group>
                                <Form.Label>Notes</Form.Label>
                                <Form.Control as="textarea" rows={2} value={transfer.form.notes} onChange={(e) => setTransferField("notes", e.target.value)} />
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setTransfer(null)}>Cancel</Button>
                            <Button type="submit" variant="success" disabled={busy || !transfer.form.reference}>Save transfer</Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Modal>

            <Modal show={!!deduction} onHide={() => setDeduction(null)} centered>
                {deduction && (
                    <Form onSubmit={saveDeduction}>
                        <Modal.Header closeButton><Modal.Title>Other deductions</Modal.Title></Modal.Header>
                        <Modal.Body>
                            <Form.Group className="mb-2">
                                <Form.Label>Amount (Rs)</Form.Label>
                                <Form.Control type="number" min="0" step="0.01" value={deduction.amount} onChange={(e) => setDeduction({ ...deduction, amount: e.target.value })} />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Reason (shown in the seller&apos;s ledger)</Form.Label>
                                <Form.Control value={deduction.note} onChange={(e) => setDeduction({ ...deduction, note: e.target.value })} />
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setDeduction(null)}>Cancel</Button>
                            <Button type="submit" disabled={busy}>Save</Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Modal>
        </PageLayout>
    );
}
